import { shortSuiAddress, suiLinkMessage } from "@act/shared";
import type { AudioService } from "../audio/AudioService";
import type { AccountFailure, AccountService, AccountView } from "../account/AccountService";
import { renderGoogleButton } from "../account/googleIdentity";
import { linkableAccounts } from "../account/linkable";
import type { SuiApp, SuiAppAccount } from "../account/suiApps";
import { panelCloseButton, panelClosed, panelOpened } from "./panelChrome";

type SuiAppsModule = typeof import("../account/suiApps");

/** Player-facing words for each failure. Never "wallet" or "NFT" (ACT HQ copy rule). */
const FAILURE_HINTS: Record<AccountFailure, string> = {
  unauthenticated: "sign in again",
  origin_rejected: "not available on this page",
  invalid_request: "something went wrong — try again",
  invalid_token: "Google sign-in expired — try again",
  identity_mismatch: "that's a different Google account",
  sign_in_first: "sign in with Google first",
  stale_message: "that took too long — try again",
  bad_signature: "that signature didn't check out",
  address_is_account_id: "that address is already an ACT Account ID",
  account_id_unavailable: "Account IDs aren't switched on yet",
  unavailable: "couldn't reach the server — try again",
  offline: "offline",
  google_failed: "Google sign-in didn't go through",
};

/** The account's name, as the panel shows it, and the way to change it (the name prompt). */
export type AccountNames = {
  current: () => string;
  edit: () => void;
  onChanged: (listener: (name: string) => void) => () => void;
};

/** Where "Link Sui" is: each click moves it on (wallets and Slush web need a click per popup). */
type LinkStep =
  | { at: "idle" }
  | { at: "loading" }
  | { at: "choose-app"; apps: SuiApp[] }
  | { at: "choose-account"; app: SuiApp; accounts: SuiAppAccount[] }
  | { at: "sign"; app: SuiApp; account: SuiAppAccount }
  /** The app shared only addresses this account has already linked. */
  | { at: "all-linked"; app: SuiApp; shared: number }
  /** The app forgot ACT: the next connect (a fresh click) shows its address picker. */
  | { at: "pick-again"; app: SuiApp }
  | { at: "waiting"; label: string };

/**
 * Account panel (DR-0053; ACT HQ DR-0009 as changed 29 Sep 2026). DOM overlay in
 * the FriendsPanel pattern, opened from the main menu's "Account" item. A guest
 * sees "Sign in with Google"; a Google account sees its Account ID, its linked
 * Sui addresses, "Link Sui" and sign out.
 */
export class AccountPanel {
  private overlay: HTMLElement | null = null;
  private body: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private link: LinkStep = { at: "idle" };
  private suiApps: Promise<SuiAppsModule> | null = null;
  /** The loaded Link Sui module: kept so a click reaches the Sui app with no await first (popups need the click). */
  private suiMod: SuiAppsModule | null = null;
  private offApps: (() => void) | null = null;
  private busy = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(
    private readonly hudRoot: HTMLElement,
    private readonly account: AccountService,
    private readonly audio: AudioService | null = null,
    private readonly names: AccountNames | null = null,
  ) {}

  isOpen(): boolean {
    return this.overlay !== null;
  }

  open(): void {
    if (this.overlay) return;
    const overlay = document.createElement("div");
    overlay.className = "results-overlay menu-overlay account-overlay";
    const card = document.createElement("div");
    card.className = "results-card menu-card account-card";

    const heading = document.createElement("div");
    heading.className = "results-title";
    heading.textContent = "Account";
    card.appendChild(heading);
    card.appendChild(panelCloseButton(() => this.close()));

    const status = document.createElement("div");
    status.className = "account-status";
    card.appendChild(status);
    this.statusEl = status;

    const body = document.createElement("div");
    body.className = "account-body";
    card.appendChild(body);
    this.body = body;

    const row = document.createElement("div");
    row.className = "results-buttons";
    row.appendChild(this.button("close", () => this.close()));
    card.appendChild(row);

    overlay.appendChild(card);
    this.hudRoot.appendChild(overlay);
    this.overlay = overlay;
    panelOpened(overlay, () => this.close(), this.audio);

    this.unsubs.push(this.account.onChanged.on(() => this.render()));
    if (this.names) this.unsubs.push(this.names.onChanged(() => this.render()));
    this.render();
    void this.account.refresh();
    // Read the wallets again too (ACT HQ t-042): something bought or sold since shows up.
    void this.account.refreshHoldings();
  }

  close(): void {
    for (const off of this.unsubs.splice(0)) off();
    this.offApps?.();
    this.offApps = null;
    if (this.overlay) panelClosed(this.overlay, this.audio);
    this.overlay?.remove();
    this.overlay = null;
    this.body = null;
    this.statusEl = null;
    this.link = { at: "idle" };
  }

  private render(): void {
    const body = this.body;
    if (!body) return;
    body.replaceChildren();
    const view = this.account.current();
    if (!view.signedIn) {
      body.appendChild(this.hint("guest — this account lives in this browser only. Sign in to keep it on any device."));
      body.appendChild(this.googleButton());
      return;
    }
    body.appendChild(this.row("signed in", this.text(view.email ?? "Google", "account-email")));
    // The name other players see, up front (Theo, 3 Oct 2026: setting it was "a bit hidden").
    if (this.names) {
      const names = this.names;
      const name = names.current();
      const wrap = document.createElement("span");
      wrap.className = "account-id";
      wrap.appendChild(this.text(name || "not set", name ? "account-name" : "account-pending"));
      wrap.appendChild(this.button(name ? "change" : "set", () => names.edit()));
      body.appendChild(this.row("name", wrap));
    }
    body.appendChild(this.accountIdRow(view));
    // One slot per linked address, as many as the player likes (ACT HQ t-706).
    body.appendChild(this.sectionTitle(view.linked.length > 0 ? `LINKED SUI · ${view.linked.length}` : "LINKED SUI"));
    if (view.linked.length === 0) body.appendChild(this.hint("none yet"));
    for (const address of view.linked) {
      body.appendChild(this.row(shortSuiAddress(address), this.button("remove", () => void this.unlink(address)), address, "sui-address"));
    }
    // What the game read from the Account ID and these addresses (ACT HQ t-042).
    if (view.walletItems !== null) body.appendChild(this.row("in game", this.text(`${view.walletItems} item${view.walletItems === 1 ? "" : "s"}`, "account-items")));
    body.appendChild(this.linkArea(view));
    const signOut = this.button("sign out", () => void this.signOut());
    signOut.classList.add("account-sign-out");
    body.appendChild(signOut);
  }

  private accountIdRow(view: AccountView): HTMLElement {
    if (view.accountId) {
      const id = view.accountId;
      const wrap = document.createElement("span");
      wrap.className = "account-id";
      wrap.appendChild(this.text(shortSuiAddress(id), "account-address"));
      wrap.appendChild(this.button("copy", () => void this.copy(id)));
      return this.row("Account ID", wrap, id);
    }
    if (view.accountIdWaiting === "unavailable") return this.row("Account ID", this.text("coming soon", "account-pending"));
    // Only a fresh Google token can make it (the server asks Enoki with it).
    const wrap = document.createElement("div");
    wrap.appendChild(this.row("Account ID", this.text("not made yet", "account-pending")));
    wrap.appendChild(this.hint("sign in with Google again to make it"));
    wrap.appendChild(this.googleButton());
    return wrap;
  }

  private googleButton(): HTMLElement {
    const host = document.createElement("div");
    host.className = "account-google";
    renderGoogleButton(host, (idToken, rawNonce) => void this.signIn(idToken, rawNonce)).catch(() => {
      host.replaceChildren(this.hint("Google sign-in couldn't load here. Try another browser."));
    });
    return host;
  }

  private async signIn(idToken: string, rawNonce: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.showStatus("signing in…");
    try {
      const result = await this.account.signInWithGoogle(idToken, rawNonce);
      if (!result.ok) {
        this.fail(result.error);
        return;
      }
      this.playUi("ui.confirm");
      if (result.switched) {
        // This Google login already had an account: that one wins. Everything on
        // the page was wired to the old account, so start again as the new one.
        this.showStatus("signed in — loading your account…");
        setTimeout(() => location.reload(), 600);
        return;
      }
      const view = this.account.current();
      this.showStatus(view.accountId ? "signed in" : "signed in — Account ID not made yet");
    } finally {
      this.busy = false;
    }
  }

  // --- Link Sui -------------------------------------------------------------

  private linkArea(view: AccountView): HTMLElement {
    const area = document.createElement("div");
    area.className = "account-link";
    const step = this.link;
    switch (step.at) {
      case "idle":
        area.appendChild(this.button(view.linked.length > 0 ? "link another" : "Link Sui", () => this.startLink()));
        break;
      case "loading":
        area.appendChild(this.hint("looking for Sui apps…"));
        break;
      case "choose-app":
        area.appendChild(this.hint(step.apps.length ? "link with:" : "no Sui app found in this browser"));
        for (const app of step.apps) area.appendChild(this.appButton(app));
        area.appendChild(this.button("cancel", () => this.setLink({ at: "idle" })));
        break;
      case "choose-account":
        area.appendChild(this.hint(`which ${step.app.name} address?`));
        for (const account of step.accounts) {
          const pick = this.button(shortSuiAddress(account.address), () => this.setLink({ at: "sign", app: step.app, account }));
          pick.classList.add("sui-address");
          area.appendChild(pick);
        }
        area.appendChild(this.button("cancel", () => this.setLink({ at: "idle" })));
        break;
      case "sign": {
        area.appendChild(this.hint(`sign a message in ${step.app.name} to link ${shortSuiAddress(step.account.address)}`));
        const sign = this.button("sign to link", () => void this.sign(view, step.app, step.account));
        area.appendChild(sign);
        area.appendChild(this.button("cancel", () => this.setLink({ at: "idle" })));
        break;
      }
      case "all-linked": {
        const what = step.shared === 1 ? "the address it shared is" : `all ${step.shared} addresses it shared are`;
        area.appendChild(this.hint(`${step.app.name}: ${what} already linked`));
        if (this.suiMod?.canChooseAgain(step.app)) {
          area.appendChild(this.button(`choose another in ${step.app.name}`, () => void this.chooseAgain(step.app)));
        } else {
          area.appendChild(this.hint(`switch to another address in ${step.app.name}, then link another`));
        }
        area.appendChild(this.button("cancel", () => this.setLink({ at: "idle" })));
        break;
      }
      case "pick-again":
        area.appendChild(this.hint(`pick the address to link in ${step.app.name}`));
        area.appendChild(this.button(`open ${step.app.name}`, () => void this.connect(step.app)));
        area.appendChild(this.button("cancel", () => this.setLink({ at: "idle" })));
        break;
      case "waiting":
        area.appendChild(this.hint(step.label));
        break;
    }
    return area;
  }

  private startLink(): void {
    this.showStatus("");
    this.setLink({ at: "loading" });
    this.suiApps ??= import("../account/suiApps");
    this.suiApps
      .then((mod) => {
        this.suiMod = mod;
        if (this.link.at !== "loading") return;
        const show = () => {
          if (this.link.at === "loading" || this.link.at === "choose-app") this.setLink({ at: "choose-app", apps: mod.listSuiApps() });
        };
        this.offApps ??= mod.onSuiAppsChanged(show);
        show();
      })
      .catch(() => {
        this.suiApps = null;
        this.setLink({ at: "idle" });
        this.fail("unavailable");
      });
  }

  private appButton(app: SuiApp): HTMLElement {
    const btn = this.button(app.name, () => void this.connect(app));
    if (app.icon) {
      const img = document.createElement("img");
      img.src = app.icon;
      img.alt = "";
      img.className = "account-app-icon";
      btn.prepend(img);
    }
    btn.classList.add("account-app");
    return btn;
  }

  private async connect(app: SuiApp): Promise<void> {
    const mod = this.suiMod;
    if (!mod) return;
    this.setLink({ at: "waiting", label: `approve in ${app.name}…` });
    try {
      const accounts = await mod.connectSuiApp(app);
      // An app that already let ACT in hands back what it shared last time: skip
      // what this account has, so the player is never asked to link it twice.
      const view = this.account.current();
      const fresh = linkableAccounts(accounts, [...view.linked, view.accountId]);
      if (accounts.length === 0) {
        this.setLink({ at: "idle" });
        this.showStatus(`${app.name} shared no Sui address`, true);
      } else if (fresh.length === 0) {
        this.setLink({ at: "all-linked", app, shared: accounts.length });
      } else if (fresh.length === 1) {
        this.setLink({ at: "sign", app, account: fresh[0]! });
      } else {
        this.setLink({ at: "choose-account", app, accounts: fresh });
      }
    } catch {
      this.setLink({ at: "idle" });
      this.showStatus("cancelled", true);
    }
  }

  /** Have the app forget ACT, then wait for a fresh click to connect (popups need one). */
  private async chooseAgain(app: SuiApp): Promise<void> {
    const mod = this.suiMod;
    if (!mod) return;
    this.setLink({ at: "waiting", label: `resetting ${app.name}…` });
    try {
      await mod.disconnectSuiApp(app);
    } catch {
      // Some apps refuse or ignore it; connecting again still lets the player switch inside the app.
    }
    this.setLink({ at: "pick-again", app });
  }

  private async sign(view: AccountView, app: SuiApp, account: SuiAppAccount): Promise<void> {
    const mod = this.suiMod;
    if (!mod || !view.userId) return;
    const message = suiLinkMessage({ address: account.address, accountUuid: view.userId, issuedAt: new Date() });
    this.setLink({ at: "waiting", label: `sign in ${app.name}…` });
    let signature: string;
    try {
      signature = await mod.signWithSuiApp(app, account, message);
    } catch {
      this.setLink({ at: "idle" });
      this.showStatus("cancelled", true);
      return;
    }
    this.setLink({ at: "waiting", label: "checking…" });
    const result = await this.account.linkSui({ address: account.address, message, signature });
    this.setLink({ at: "idle" });
    if (!result.ok) {
      this.fail(result.error);
      return;
    }
    this.playUi("ui.confirm");
    const short = shortSuiAddress(account.address);
    this.showStatus(result.moved ? `linked ${short} (moved from another ACT account)` : `linked ${short}`);
  }

  private setLink(step: LinkStep): void {
    this.link = step;
    this.render();
  }

  // --- other actions --------------------------------------------------------

  private async unlink(address: string): Promise<void> {
    const ok = await this.account.unlink(address);
    this.showStatus(ok ? `removed ${shortSuiAddress(address)}` : "couldn't remove — try again", !ok);
  }

  private async signOut(): Promise<void> {
    await this.account.signOut();
    this.showStatus("signed out — reloading…");
    setTimeout(() => location.reload(), 400);
  }

  private async copy(address: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(address);
      this.showStatus("Account ID copied");
    } catch {
      this.showStatus(address);
    }
  }

  // --- builders -------------------------------------------------------------

  private fail(error: AccountFailure): void {
    this.playUi("ui.error");
    this.showStatus(FAILURE_HINTS[error], true);
  }

  private showStatus(text: string, isError = false): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = text;
    this.statusEl.classList.toggle("account-status-error", isError);
  }

  private playUi(event: "ui.confirm" | "ui.error" | "ui.click"): void {
    this.audio?.play(event);
  }

  private button(label: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "dbg-btn";
    btn.textContent = label;
    btn.addEventListener("click", () => {
      this.playUi("ui.click");
      onClick();
    });
    return btn;
  }

  private row(label: string, value: HTMLElement, title?: string, labelClass?: string): HTMLElement {
    const row = document.createElement("div");
    row.className = "menu-row account-row";
    const span = document.createElement("span");
    span.textContent = label;
    if (labelClass) span.className = labelClass;
    if (title) row.title = title;
    row.appendChild(span);
    row.appendChild(value);
    return row;
  }

  private text(value: string, className: string): HTMLElement {
    const span = document.createElement("span");
    span.className = className;
    span.textContent = value;
    return span;
  }

  private hint(text: string): HTMLElement {
    const div = document.createElement("div");
    div.className = "menu-hint";
    div.textContent = text;
    return div;
  }

  private sectionTitle(text: string): HTMLElement {
    const div = document.createElement("div");
    div.className = "menu-section-title";
    div.textContent = text;
    return div;
  }
}
